import { useState, useEffect, useRef, useCallback } from "react";
import { useEditor } from "@tldraw/tldraw";
import { createShapeId, TLShapeId, AssetRecordType, TLAssetId } from "@tldraw/editor";
import QRCode from "qrcode";
import { sanitizeVariableName, extractVariablesFromText, unsanitizeVariableName } from "../utils";
import { DEFAULT_PLACEHOLDERS } from "../const";
import { useStore } from "store";
import { useBrandKitStore } from "store/brandKit";

/** Variable names that should insert a QR Code shape instead of text */
export const QR_VARIABLE_NAMES = ["qr_link", "qr_code", "qrlink", "qrcode"];

/**
 * Reads the Brand Kit default font preset.
 * Checks localStorage first (set by syncFirstPresetToLocalStorage),
 * then falls back to the Zustand store (in case localStorage wasn't populated yet).
 */
/**
 * Parses a Brand Kit font preset into tldraw shape props.
 * Extracts fontWeight, italic, underline from the fontVariant string
 * (e.g. "Arbutus-Bold:i:u" → fontWeight: 700, italic: "italic", underline: "underline")
 */
function presetToShapeProps(preset: {
  fontFamily?: string;
  fontVariant?: string;
  fontSize?: number;
  lineHeight?: number;
  letterSpacing?: number;
}): Record<string, any> {
  const props: Record<string, any> = {};

  // Use fontVariant base (without :i/:u flags) as the font name.
  // This matches how TextParameters.handleApplyFontPreset sets the font:
  // - Custom CDN fonts: font = baseVariant (e.g. "AdobeClean-ExtraBoldIt")
  // - Standard fonts: font = fontFamily (e.g. "Roboto") — baseVariant is the same as fontFamily for regular weight
  const baseVariant = preset.fontVariant ? preset.fontVariant.split(":")[0] : null;
  if (baseVariant) {
    props.font = baseVariant;
  } else if (preset.fontFamily) {
    props.font = preset.fontFamily;
  }

  if (preset.fontSize) props.size = preset.fontSize;
  if (preset.lineHeight) props.lineHeight = preset.lineHeight;
  if (preset.letterSpacing) props.letterSpacing = preset.letterSpacing;

  // Parse fontVariant for weight, italic, underline
  // Italic can be encoded as: ":i" flag, "It" suffix, or "Italic" suffix
  // Weight can be encoded as: suffix like "-Bold", "-ExtraBold", "-Light", "-ExtraBoldIt" etc.
  if (preset.fontVariant) {
    const variantParts = preset.fontVariant.split(":");
    const baseVariant = variantParts[0];
    const applyItalicFlag = variantParts.includes("i");
    const applyUnderlineFlag = variantParts.includes("u");

    // Extract suffix after fontFamily name
    let suffix = "";
    if (preset.fontFamily) {
      suffix = baseVariant.slice(preset.fontFamily.length).replace(/^[-_]/, "").toLowerCase();
    }

    // Detect italic from suffix ("it", "italic") or flag (":i")
    const hasItalicSuffix = suffix.endsWith("it") || suffix.endsWith("italic");
    if (applyItalicFlag || hasItalicSuffix) props.italic = "italic";
    if (applyUnderlineFlag) props.underline = "underline";

    // Strip italic suffix before parsing weight
    const weightSuffix = suffix.replace(/i(?:t(?:alic)?)?$/, "").trim();

    // Map weight suffix to CSS fontWeight
    const WEIGHT_MAP: Record<string, number> = {
      thin: 100,
      hairline: 100,
      extralight: 200,
      ultralight: 200,
      light: 300,
      "": 400,
      regular: 400,
      medium: 500,
      semibold: 600,
      "semi-bold": 600,
      demibold: 600,
      bold: 700,
      extrabold: 800,
      "extra-bold": 800,
      ultrabold: 800,
      black: 900,
      heavy: 900,
    };

    if (weightSuffix && WEIGHT_MAP[weightSuffix] !== undefined) {
      props.fontWeight = WEIGHT_MAP[weightSuffix];
    }
  }

  return props;
}

function getBrandKitFontProps(storePresets?: any[]): Record<string, any> {
  try {
    // 1. Try localStorage (fastest, set by Brand Kit store on load)
    const presetStr = localStorage.getItem("brandDefaultFontPreset");
    if (presetStr) {
      const preset = JSON.parse(presetStr);
      const props = presetToShapeProps(preset);
      return props;
    }
    // 2. Fallback: last selected font in localStorage
    const lastFont = localStorage.getItem("lastSelectedFont");
    if (lastFont) return { font: lastFont };

    // 3. Fallback: read directly from Zustand store (for fresh sessions where localStorage is empty)
    if (storePresets && storePresets.length > 0) {
      const p = storePresets[0];
      return presetToShapeProps(p);
    }
  } catch (e) {
    console.error("[getBrandKitFontProps] Error:", e);
  }
  return {};
}

/**
 * Custom hook to integrate VariablesPanel with Tldraw editor
 * @returns Object with onAddVariable handler and utility functions
 */
export const useTldrawIntegration = () => {
  const [selectedVariables, setSelectedVariables] = useState<{ id: string; name: string }[]>([]);
  const editor = useEditor();
  const { qrCodeSettings } = useStore();
  const fontPresets = useBrandKitStore((s) => s.fontPresets);
  const qrPixelColor = qrCodeSettings?.qrPixelColor || "#000000";
  const qrBgColor = qrCodeSettings?.qrBackgroundColor || "#FFFFFF";
  const onVariableRemovedFromCanvasRef = useRef<((variableName: string) => void) | null>(null);
  const selectedVariablesRef = useRef<{ id: string; name: string }[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    selectedVariablesRef.current = selectedVariables;
  }, [selectedVariables]);

  /**
   * Inserts a QR Code placeholder shape (customImage with qrCodeLink) into the canvas.
   * Mirrors the creation logic from QrCode.tsx sidebar component.
   */
  const insertQRCodeShape = useCallback(async () => {
    // Check if a QR code already exists on the canvas
    const currentPageId = editor.getCurrentPageId();
    const allShapeIds = editor.getSortedChildIdsForParent(currentPageId);
    const existingQR = allShapeIds.find((id) => {
      const shape = editor.getShape(id);
      if (!shape || shape.type !== "customImage") return false;
      const sid = shape.id as string;
      return sid.includes("qrCode") || (shape as any).props?.assetId?.toString().includes("qr");
    });

    if (existingQR) {
      // QR already exists — just select it
      editor.setCurrentTool("select");
      editor.select(existingQR);
      return;
    }

    // Generate a placeholder QR SVG
    const placeholderUrl = "https://oppizi.com";
    const svgString = await QRCode.toString(placeholderUrl, {
      type: "svg",
      margin: 0,
      width: 300,
      color: { dark: qrPixelColor, light: qrBgColor },
    });
    const base64Src = `data:image/svg+xml;base64,${btoa(svgString)}`;

    const shapeId = createShapeId();
    const assetId = AssetRecordType.createId(`qrCode-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

    // Create the image asset
    editor.createAssets([
      {
        id: assetId as TLAssetId,
        type: "image",
        typeName: "asset",
        meta: {},
        props: {
          name: "image",
          src: base64Src,
          w: 150,
          h: 150,
          mimeType: "image/svg+xml",
          isAnimated: false,
        },
      },
    ]);

    // Place at center of viewport
    const centerPoint = editor.getViewportScreenCenter();
    const pagePoint = editor.screenToPage(centerPoint);

    // Create the customImage shape with qrCodeLink prop
    editor.createShape({
      id: shapeId as any,
      type: "customImage",
      x: pagePoint.x - 75,
      y: pagePoint.y - 75,
      props: {
        w: 150,
        h: 150,
        assetId: assetId,
        qrCodeLink: placeholderUrl,
      },
    });

    editor.setCurrentTool("select");
    editor.select(shapeId);
    setSelectedVariables((prev) => [...prev, { id: shapeId as string, name: "qr_link" }]);
  }, [editor, qrPixelColor, qrBgColor]);

  /**
   * Handles variable click by inserting the sanitized variable name into selected text elements
   * or creating a new text element if none is selected.
   * Special case: qr_link inserts a QR Code shape (customImage) instead of text.
   * @param variableName - The original variable name
   */
  const onAddVariable = (variableName: string) => {
    const sanitizedVariable = sanitizeVariableName(variableName);

    // Special handling for QR link variables — insert a QR Code shape
    if (QR_VARIABLE_NAMES.includes(sanitizedVariable)) {
      insertQRCodeShape();
      return;
    }

    const variablePlaceholder = `{{${sanitizedVariable}}}`;
    const newShapeId = createShapeId();
    const brandFontProps = getBrandKitFontProps(fontPresets);

    // Check if the user is actively editing a text shape (cursor inside the text).
    // Only append to the existing text if in edit mode — otherwise create a new text shape.
    const editingShapeId = editor.getEditingShapeId();
    if (editingShapeId) {
      const editingShape = editor.getShape(editingShapeId);
      if (editingShape && (editingShape.type === "customText" || editingShape.type === "textarea")) {
        // User is editing a text shape — append the placeholder to it
        const currentText = (editingShape as any).text || (editingShape as any).props?.text || "";
        const newText = currentText + variablePlaceholder;
        editor.updateShape({
          ...editingShape,
          props: { ...(editingShape as any).props, text: newText },
        } as any);
        // Exit and re-enter edit mode to force the text shape to re-render
        // so the new placeholder text becomes visible immediately
        editor.setEditingShape(null);
        requestAnimationFrame(() => {
          editor.setEditingShape(editingShapeId);
        });
        return setSelectedVariables((prev) => [...prev, { id: editingShapeId as string, name: variableName }]);
      }
    }

    // Not in edit mode (or no text being edited) — always create a new text shape
    const centerPoint = editor.getViewportScreenCenter();
    const pagePoint = editor.screenToPage(centerPoint);
    editor.createShapes([
      {
        id: newShapeId,
        type: "customText",
        x: pagePoint.x,
        y: pagePoint.y,
        props: {
          text: variablePlaceholder,
          ...brandFontProps,
        },
      },
    ]);
    return setSelectedVariables((prev) => [...prev, { id: newShapeId, name: variableName }]);
  };

  const onRemoveVariable = (variableName: string) => {
    const sanitizedVariable = sanitizeVariableName(variableName);

    // Special handling: if removing qr_link, delete QR Code shapes from canvas
    if (QR_VARIABLE_NAMES.includes(sanitizedVariable)) {
      const currentPageId = editor.getCurrentPageId();
      const allShapeIds = editor.getSortedChildIdsForParent(currentPageId);
      const qrShapesToDelete: string[] = [];

      allShapeIds.forEach((shapeId) => {
        const shape = editor.getShape(shapeId);
        if (!shape || shape.type !== "customImage") return;
        const sid = shape.id as string;
        const assetId = (shape as any).props?.assetId;
        const isQR =
          sid.includes("qrCode") || (assetId && assetId.toString().includes("qr")) || (shape as any).props?.qrCodeLink;
        if (isQR) {
          qrShapesToDelete.push(shapeId);
        }
      });

      if (qrShapesToDelete.length > 0) {
        editor.deleteShapes(qrShapesToDelete as TLShapeId[]);
        setSelectedVariables((prev) => prev.filter((v) => !qrShapesToDelete.includes(v.id) && v.name !== variableName));
      }
      return;
    }

    const variablePlaceholder = `{{${sanitizedVariable}}}`;

    // Find all shapes that contain this variable
    const currentPageId = editor.getCurrentPageId();
    const allShapeIds = editor.getSortedChildIdsForParent(currentPageId);
    const shapesToUpdate: { id: string; shape: any }[] = [];
    const shapesToDelete: string[] = [];

    allShapeIds.forEach((shapeId) => {
      const shape = editor.getShape(shapeId);

      if (shape && (shape.type === "customText" || shape.type === "textarea")) {
        const text = (shape as any).text || (shape as any).props?.text || "";

        // Check if this shape contains the variable
        if (text.includes(variablePlaceholder)) {
          // Remove the variable placeholder from the text
          const newText = text.replace(variablePlaceholder, "");

          // If the text becomes empty or only whitespace, delete the shape
          if (newText.trim() === "") {
            shapesToDelete.push(shapeId);
          } else {
            // Otherwise, update the shape with the new text
            shapesToUpdate.push({ id: shapeId, shape });

            if (shape.type === "customText") {
              editor.updateShape({
                ...shape,
                props: {
                  ...(shape as any).props,
                  text: newText,
                },
              } as any);
            } else if (shape.type === "textarea") {
              editor.updateShape({
                ...shape,
                props: {
                  ...(shape as any).props,
                  text: newText,
                },
              } as any);
            }
          }
        }
      }
    });

    // Delete shapes that became empty
    if (shapesToDelete.length > 0) {
      editor.deleteShapes(shapesToDelete as TLShapeId[]);
    }

    // Update selectedVariables to remove entries for deleted shapes or the removed variable
    if (shapesToDelete.length > 0 || shapesToUpdate.length > 0) {
      setSelectedVariables((prev) =>
        prev.filter((v) => {
          // Remove entries for deleted shapes
          if (shapesToDelete.includes(v.id)) {
            return false;
          }
          // Remove entries for the variable being removed (even if shape still exists with other variables)
          if (v.name === variableName) {
            return false;
          }
          return true;
        })
      );
    }
  };

  // Listen for shape deletions from canvas to sync with VariablesPanelV2
  useEffect(() => {
    const unsubscribe = editor.store.listen(
      ({ changes }: any) => {
        // Check if any shapes were removed (skip during preview)
        if (isApplyingPreviewRef.current) return;
        if (changes.removed && Object.keys(changes.removed).length > 0) {
          const removedIds = Object.keys(changes.removed);

          // Find variables that were removed using ref to get latest state
          const removedVariables = selectedVariablesRef.current.filter((v) => removedIds.includes(v.id));

          // Update selectedVariables state and notify parent
          if (removedVariables.length > 0) {
            setSelectedVariables((prev) => prev.filter((v) => !removedIds.includes(v.id)));

            // Notify VariablesPanelV2 about removed variables
            removedVariables.forEach((variable) => {
              if (onVariableRemovedFromCanvasRef.current) {
                onVariableRemovedFromCanvasRef.current(variable.name);
              }
            });
          }
        }
      },
      { source: "user", scope: "document" }
    );

    return () => {
      unsubscribe();
    };
  }, [editor]);

  // Function to register callback for variable removal from canvas
  const registerVariableRemovalCallback = (callback: (variableName: string) => void) => {
    onVariableRemovedFromCanvasRef.current = callback;
  };

  // Function to scan canvas and get all active variables
  const getActiveVariablesFromCanvas = useCallback((): string[] => {
    const activeVariableNames = new Set<string>();

    try {
      // Get all shapes from the current page
      const currentPageId = editor.getCurrentPageId();
      const allShapeIds = editor.getSortedChildIdsForParent(currentPageId);

      allShapeIds.forEach((shapeId) => {
        const shape = editor.getShape(shapeId);
        if (!shape) return;

        // Detect text variables
        if (shape.type === "customText" || shape.type === "textarea") {
          const text = (shape as any).text || (shape as any).props?.text || "";
          const sanitizedVariables = extractVariablesFromText(text);
          sanitizedVariables.forEach((sanitizedVar) => {
            const originalName = unsanitizeVariableName(sanitizedVar, DEFAULT_PLACEHOLDERS);
            activeVariableNames.add(originalName);
          });
        }

        // Detect QR Code shapes → report as "qr_link"
        if (shape.type === "customImage") {
          const sid = shape.id as string;
          const assetId = (shape as any).props?.assetId;
          const isQR =
            sid.includes("qrCode") ||
            (assetId && assetId.toString().includes("qr")) ||
            (shape as any).props?.qrCodeLink;
          if (isQR) {
            activeVariableNames.add("qr_link");
          }
        }
      });
    } catch (error) {
      console.error("Error scanning canvas for variables:", error);
    }

    return Array.from(activeVariableNames);
  }, [editor]);

  // Callback ref for when variables change in canvas
  const onVariablesChangeRef = useRef<(() => void) | null>(null);

  // Listen for shape changes (additions, updates, removals) to sync with VariablesPanelV2
  useEffect(() => {
    const unsubscribe = editor.store.listen(
      ({ changes }: any) => {
        // Check if any shapes were added, updated, or removed
        const hasChanges =
          (changes.added && Object.keys(changes.added).length > 0) ||
          (changes.updated && Object.keys(changes.updated).length > 0) ||
          (changes.removed && Object.keys(changes.removed).length > 0);

        if (hasChanges && onVariablesChangeRef.current && !isApplyingPreviewRef.current) {
          // Notify that variables may have changed (skip during preview)
          onVariablesChangeRef.current();
        }
      },
      { source: "user", scope: "document" }
    );

    return () => {
      unsubscribe();
    };
  }, [editor]);

  // Function to register callback for when variables change in canvas
  const registerVariablesChangeCallback = (callback: () => void) => {
    onVariablesChangeRef.current = callback;
  };

  // Preview mode: store original text and QR assets so we can restore them
  const originalTextsRef = useRef<Map<string, string>>(new Map());
  const originalQRAssetsRef = useRef<Map<string, { assetId: string; src: string }>>(new Map());
  /** Flag to suppress change callbacks during preview apply/clear */
  const isApplyingPreviewRef = useRef(false);

  /**
   * Replaces {{variable}} placeholders on the canvas with actual preview values.
   * Also replaces QR Code shapes with QR images generated from qr_link values.
   * Stores originals for restoration via clearPreview().
   */
  const applyPreview = useCallback(
    async (previewValues: Record<string, string>) => {
      isApplyingPreviewRef.current = true;

      // First restore any existing preview
      if (originalTextsRef.current.size > 0 || originalQRAssetsRef.current.size > 0) {
        clearPreviewInternal();
      }

      const currentPageId = editor.getCurrentPageId();
      const allShapeIds = editor.getSortedChildIdsForParent(currentPageId);

      // Find qr_link value from preview data
      const qrLinkValue = previewValues["qr_link"] || previewValues["qrlink"] || previewValues["qr_code"] || null;

      allShapeIds.forEach((shapeId) => {
        const shape = editor.getShape(shapeId);
        if (!shape) return;

        // Handle text shapes
        if (shape.type === "customText" || shape.type === "textarea") {
          const text = (shape as any).text || (shape as any).props?.text || "";
          const variableRegex = /\{\{([^}]+)\}\}/g;

          if (!variableRegex.test(text)) return;

          originalTextsRef.current.set(shapeId as string, text);

          const previewText = text.replace(/\{\{([^}]+)\}\}/g, (_match: string, varName: string) => {
            const trimmed = varName.trim();
            // Direct match
            if (previewValues[trimmed] !== undefined) return previewValues[trimmed];
            // Title Case match (postal_code → Postal Code)
            const titleCase = trimmed
              .split("_")
              .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ");
            if (previewValues[titleCase] !== undefined) return previewValues[titleCase];
            // Case-insensitive match (handles "Postal code" vs "Postal Code" etc.)
            const lowerTrimmed = trimmed.toLowerCase();
            const ciKey = Object.keys(previewValues).find(
              (k) => k.toLowerCase() === lowerTrimmed || k.toLowerCase().replace(/\s+/g, "_") === lowerTrimmed
            );
            if (ciKey !== undefined) return previewValues[ciKey];
            return _match;
          });

          editor.updateShape({
            ...shape,
            props: { ...(shape as any).props, text: previewText },
          } as any);
        }
      });

      // Handle QR Code shapes — replace asset image with generated QR
      if (qrLinkValue) {
        for (const shapeId of allShapeIds) {
          const shape = editor.getShape(shapeId);
          if (!shape || shape.type !== "customImage") continue;

          const sid = shape.id as string;
          const assetId = (shape as any).props?.assetId;
          const isQR =
            sid.includes("qrCode") ||
            (assetId && assetId.toString().includes("qr")) ||
            (shape as any).props?.qrCodeLink;

          if (!isQR) continue;

          // Store original asset src for restoration
          const asset = assetId ? editor.getAsset(assetId) : null;
          if (asset && (asset as any).props?.src) {
            originalQRAssetsRef.current.set(shapeId as string, {
              assetId: assetId as string,
              src: (asset as any).props.src,
            });

            // Generate new QR code from preview value
            try {
              const svgString = await QRCode.toString(qrLinkValue, {
                type: "svg",
                margin: 0,
                width: 300,
                color: { dark: qrPixelColor, light: qrBgColor },
              });
              const newSrc = `data:image/svg+xml;base64,${btoa(svgString)}`;

              // Update the asset with the new QR image
              editor.updateAssets([
                {
                  ...asset,
                  props: { ...(asset as any).props, src: newSrc },
                } as any,
              ]);
            } catch (err) {
              console.error("Error generating QR preview:", err);
            }
          }
        }
      }

      isApplyingPreviewRef.current = false;
    },
    [editor, qrPixelColor, qrBgColor]
  );

  const clearPreviewInternal = useCallback(() => {
    isApplyingPreviewRef.current = true;
    // Restore text shapes
    originalTextsRef.current.forEach((originalText, shapeId) => {
      const shape = editor.getShape(shapeId as any);
      if (shape) {
        editor.updateShape({
          ...shape,
          props: { ...(shape as any).props, text: originalText },
        } as any);
      }
    });
    originalTextsRef.current.clear();

    // Restore QR assets
    originalQRAssetsRef.current.forEach(({ assetId, src }) => {
      const asset = editor.getAsset(assetId as any);
      if (asset) {
        editor.updateAssets([
          {
            ...asset,
            props: { ...(asset as any).props, src },
          } as any,
        ]);
      }
    });
    originalQRAssetsRef.current.clear();
    isApplyingPreviewRef.current = false;
  }, [editor]);

  /**
   * Restores original {{variable}} placeholders on the canvas.
   */
  const clearPreview = useCallback(() => {
    clearPreviewInternal();
  }, [clearPreviewInternal]);

  return {
    onAddVariable,
    onRemoveVariable,
    editor,
    registerVariableRemovalCallback,
    getActiveVariablesFromCanvas,
    registerVariablesChangeCallback,
    applyPreview,
    clearPreview,
  };
};
